/** ORDER CONFIRMATION inline — dropdown, only the fields the selected type needs,
 *  with early Customer Billing / Accounts Payable contact designation to prevent post-dispatch payment hold. */
import { useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, AlertCircle } from "lucide-react";
import { OpsBlock } from "./OpsTop";

const err = (e) => e.response?.data?.detail || e.message;
const FIELDS = {
  FORMAL_PO: [["customer_po_number", "PO Number *"], ["po_date", "PO Date *", "date"]],
  EMAIL: [["confirmed_by", "Confirmed by"], ["confirmed_at", "Confirmation date", "date"], ["confirmation_reference", "Email subject / reference"]],
  WHATSAPP: [["confirmed_by", "Confirmed by"], ["confirmed_at", "Confirmation date", "date"], ["confirmation_reference", "Message reference"]],
  TELEPHONIC: [["confirmation_reference", "Customer reference *"], ["confirmed_at", "Confirmation date *", "date"], ["confirmed_by", "Confirmed by"]],
  VERBAL: [["confirmation_reference", "Customer reference *"], ["confirmed_at", "Confirmation date *", "date"], ["confirmed_by", "Confirmed by"]],
  OTHER: [["explanation", "Explain the confirmation *"], ["confirmed_at", "Confirmation date", "date"]],
};
const AUTO = ["DIGITAL_QUOTE", "CUSTOMER_PORTAL"];
const inp = "h-8 rounded-md border border-slate-300 px-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-slate-900/10";

export function ConfirmationSection({ qid, st, reload }) {
  const c = st.confirmation || {};
  const fromAcc = { EMAIL: "EMAIL", WHATSAPP: "WHATSAPP", DIGITAL_QUOTE: "DIGITAL_QUOTE", E_SIGN: "DIGITAL_QUOTE", CUSTOMER_PORTAL: "CUSTOMER_PORTAL", PORTAL: "CUSTOMER_PORTAL" }[st.acceptance?.acceptance_channel];
  const [t, setT] = useState(c.confirmation_type || (st.acceptance?.po_number ? "FORMAL_PO" : fromAcc) || "FORMAL_PO");
  const [f, setF] = useState({
    customer_po_number: c.customer_po_number || st.acceptance?.po_number || "",
    po_date: c.po_date || "",
    confirmation_reference: c.confirmation_reference || "",
    confirmed_at: (c.confirmed_at || "").slice(0, 10),
    confirmed_by: c.confirmed_by || "",
    explanation: c.explanation || "",
    billing_email: c.billing_email || st.order?.billing_email || st.billing_email || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    const body = {
      confirmation_type: t,
      billing_email: f.billing_email?.trim() || null,
      ...Object.fromEntries((FIELDS[t] || []).map(([k]) => [k, f[k] || null])),
    };
    try {
      await api.put(`/quote-operations/quotes/${qid}/confirmation`, body);
      toast.success("Order confirmation & billing contact saved");
      reload();
    } catch (e) {
      toast.error(err(e));
    }
    setBusy(false);
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/quote-operations/quotes/${qid}/confirmation/po-file`, fd);
      toast.success("PO uploaded");
      reload();
    } catch (e) {
      toast.error(err(e));
    }
  };

  const done = !!c.confirmation_type && (c.confirmation_type !== "FORMAL_PO" || !!c.customer_po_number);
  const acc = st.acceptance;
  const bt = st.billing_target || {};

  return (
    <OpsBlock
      id="ops-confirmation"
      title="Order confirmation & billing contact"
      done={done}
      right={
        acc && (
          <span className="text-[11px] text-slate-500" data-testid="ops-accepted-by">
            Accepted by {acc.accepted_by_name || acc.email}
            {acc.created_at ? ` · ${new Date(acc.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}` : ""}
          </span>
        )
      }
    >
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Confirmation type
          <select
            value={t}
            onChange={(e) => setT(e.target.value)}
            className={`${inp} mt-0.5 block w-44`}
            data-testid="ops-conf-type"
          >
            {st.confirmation_types.map((x) => (
              <option key={x.key} value={x.key}>{x.label}</option>
            ))}
          </select>
        </label>

        {AUTO.includes(t) ? (
          <span className="pb-1.5 text-[12px] text-slate-600" data-testid="ops-conf-auto">
            Recorded automatically from {t === "DIGITAL_QUOTE" ? "Digital Quote acceptance" : "Customer Portal"}.
          </span>
        ) : (
          (FIELDS[t] || []).map(([k, l, type]) => (
            <label key={k} className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {l}
              <input
                type={type || "text"}
                value={f[k]}
                onChange={(e) => setF({ ...f, [k]: e.target.value })}
                className={`${inp} mt-0.5 block ${k === "explanation" ? "w-72" : "w-44"}`}
                data-testid={`ops-conf-${k}`}
              />
            </label>
          ))
        )}

        {/* Early Customer Billing Contact Designation Gate */}
        <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Billing / AP Email
          <input
            type="email"
            value={f.billing_email}
            onChange={(e) => setF({ ...f, billing_email: e.target.value })}
            placeholder="finance@customer.com"
            className={`${inp} mt-0.5 block w-48 font-mono text-[11px]`}
            title="Invoices and payment receipts are sent only to this finance email — never to device recipients."
            data-testid="ops-conf-billing-email"
          />
        </label>

        {t === "FORMAL_PO" && (
          <label className="cursor-pointer rounded-md border border-dashed border-slate-400 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50" data-testid="ops-conf-upload">
            {c.po_file ? "Replace PO file" : "Upload PO"}
            <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="ops-conf-file" />
          </label>
        )}

        {c.po_file && (
          <a
            href={`${API}/quote-operations/quotes/${qid}/confirmation/po-file`}
            target="_blank"
            rel="noreferrer"
            className="pb-1.5 text-[11px] font-semibold text-sky-700 underline"
            data-testid="ops-conf-view-po"
          >
            {c.po_file.filename}
          </a>
        )}

        <button
          onClick={save}
          disabled={busy}
          className="h-8 rounded-md bg-slate-900 px-3 text-[11px] font-bold text-white hover:bg-slate-700 disabled:opacity-60"
          data-testid="ops-conf-save"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1.5">
        {bt.ok ? (
          <span className="inline-flex items-center gap-1 text-emerald-700 font-medium" data-testid="ops-conf-billing-status">
            <ShieldCheck className="h-3.5 w-3.5" />
            Payment follow-ups go to {bt.email || bt.phone}{bt.name ? ` (${bt.name})` : ""}.
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-amber-700" data-testid="ops-conf-billing-status">
            <AlertCircle className="h-3.5 w-3.5" />
            {bt.message || "Enter the customer's Finance / AP email so payment reminders can run after dispatch."}
          </span>
        )}
      </div>
    </OpsBlock>
  );
}
